import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

/**
 * How many pages of results may be in the DOM at once. Cards are recycled out beyond this: without
 * a cap the grid grows without bound as the user scrolls, and the decoded images alone are enough
 * to get the tab killed by the OS on mobile.
 */
const MAX_PAGES_IN_DOM = 3;

/** How many pages of raw section HTML to keep cached. These strings are large; do not hoard them. */
const MAX_CACHED_PAGES = 4;

/** Safety valve for the loop that skips over pages which render no items. */
const MAX_EMPTY_PAGE_SKIPS = 5;

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 * @property {HTMLElement} [loader] - The loading indicator shown while fetching the next page.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * Cached section HTML by page number. Bounded by {@link MAX_CACHED_PAGES}; see `#cachePage`.
   * @type {Map<number, string>}
   */
  pages = new Map();

  /**
   * Page numbers currently rendered in the grid, ascending. This is the source of truth for which
   * page to load next, deliberately *not* `this.refs.cards` — refs are refreshed asynchronously by
   * a MutationObserver, so reading them straight after an append can yield a stale page number and
   * cause the same page to be appended twice.
   * @type {number[]}
   */
  #pagesInDom = [];

  /**
   * Pages that rendered zero items (every product filtered out server-side). Without this the
   * observer asks for the same empty page forever, re-parsing its HTML each time.
   * @type {Set<number>}
   */
  #exhaustedPages = new Set();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {boolean} - Prevents concurrent renders triggered by fast scrolling */
  #isRendering = false;

  /** @type {ReturnType<typeof setTimeout> | null} - Debounce timer for scroll observer */
  #scrollDebounceTimer = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#syncPagesInDom();

    this.#fetchPage('next');
    this.#fetchPage('previous');
    this.#observeViewMore();

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    // Clear any pending debounce timer
    if (this.#scrollDebounceTimer) {
      clearTimeout(this.#scrollDebounceTimer);
      this.#scrollDebounceTimer = null;
    }
    // Remove the filter update listener
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        (entries) => {
          // Debounce: discard bursts caused by fast scrolling, wait for scroll to settle
          if (this.#scrollDebounceTimer) clearTimeout(this.#scrollDebounceTimer);

          this.#scrollDebounceTimer = setTimeout(async () => {
            this.#scrollDebounceTimer = null;

            // Wait for any in-progress view transitions to finish
            if (viewTransition.current) await viewTransition.current;

            // Skip if a render is already in progress
            if (this.#isRendering) return;

            for (const entry of entries) {
              if (entry.isIntersecting) {
                // Use current refs to check which element triggered
                const { viewMorePrevious, viewMoreNext } = this.refs;

                if (entry.target === viewMorePrevious) {
                  this.#renderPreviousPage();
                } else if (entry.target === viewMoreNext) {
                  this.#renderNextPage();
                }
              }
            }
          }, 150); // 150ms quiet period — coalesces rapid-fire scroll events
        },
        {
          rootMargin: '100px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);
    resolvePromise();
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    this.#cachePage(pageNumber, pageContent);
  }

  /**
   * Stores a page's HTML, evicting the least recently used entry once the cache is full.
   *
   * @param {number} pageNumber
   * @param {string} html
   */
  #cachePage(pageNumber, html) {
    // Re-inserting moves the key to the end of the Map's iteration order, which is what makes the
    // "delete the first key" eviction below least-recently-used rather than first-in-first-out.
    this.pages.delete(pageNumber);
    this.pages.set(pageNumber, html);

    while (this.pages.size > MAX_CACHED_PAGES) {
      const oldest = this.pages.keys().next().value;
      if (oldest === undefined) break;
      this.pages.delete(oldest);
    }
  }

  async #renderNextPage() {
    const { grid } = this.refs;

    if (!grid) return;

    // Guard: prevent concurrent renders from rapid scrolling
    if (this.#isRendering) return;
    this.#isRendering = true;

    // Show the bottom loader while products are being fetched/appended
    this.refs.loader?.removeAttribute('hidden');

    try {
      // A page can legitimately render zero items (see `#exhaustedPages`). When that happens the
      // sentinel stays intersecting and will not fire the observer again, so keep walking forward
      // here until something is actually appended or we run out of pages.
      for (let attempt = 0; attempt < MAX_EMPTY_PAGE_SKIPS; attempt++) {
        const nextPage = this.#getPage('next');

        if (!nextPage || !this.#shouldUsePage(nextPage)) return;

        let nextPageItemElements = this.#getGridForPage(nextPage.page);

        if (!nextPageItemElements) {
          const promise = new Promise((res) => {
            this.#resolveNextPagePromise = res;
          });

          // Trigger the fetch for this page
          this.#fetchPage('next');

          await promise;
          nextPageItemElements = this.#getGridForPage(nextPage.page);
          if (!nextPageItemElements) return;
        }

        // An empty NodeList is truthy, so this has to be a length check. `main-collection.liquid`
        // can filter out every product on a page, and without this the same page is re-parsed and
        // re-"appended" on every observer fire.
        if (!nextPageItemElements.length) {
          this.#exhaustedPages.add(nextPage.page);
          continue;
        }

        grid.append(...nextPageItemElements);
        this.#pagesInDom.push(nextPage.page);

        this.#aspectRatioHelper?.processNewElements();

        history.pushState('', '', nextPage.url.toString());

        // Drop the oldest page off the top now that a new one is on the bottom.
        this.#trimPages('start');

        requestIdleCallback(() => {
          this.#fetchPage('next');
        });

        return;
      }
    } finally {
      // Hide loader and release the lock — runs even if an error occurs mid-render
      this.refs.loader?.setAttribute('hidden', '');
      this.#isRendering = false;
    }
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid) return;

    // Guard: prevent concurrent renders from rapid scrolling
    if (this.#isRendering) return;
    this.#isRendering = true;

    try {
      for (let attempt = 0; attempt < MAX_EMPTY_PAGE_SKIPS; attempt++) {
        const previousPage = this.#getPage('previous');
        if (!previousPage || !this.#shouldUsePage(previousPage)) return;

        let previousPageItemElements = this.#getGridForPage(previousPage.page);
        if (!previousPageItemElements) {
          const promise = new Promise((res) => {
            this.#resolvePreviousPagePromise = res;
          });

          // Trigger the fetch for this page
          this.#fetchPage('previous');

          await promise;
          previousPageItemElements = this.#getGridForPage(previousPage.page);
          if (!previousPageItemElements) return;
        }

        // See the matching comment in `#renderNextPage`: an empty NodeList is truthy.
        if (!previousPageItemElements.length) {
          this.#exhaustedPages.add(previousPage.page);
          continue;
        }

        // Store the current scroll position and height of the first element
        const scrollTop = window.scrollY;
        const firstElement = grid.firstElementChild;
        const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

        // Prepend the new elements
        grid.prepend(...previousPageItemElements);
        this.#pagesInDom.unshift(previousPage.page);

        this.#aspectRatioHelper?.processNewElements();

        history.pushState('', '', previousPage.url.toString());

        // Calculate and adjust scroll position to maintain the same view
        if (firstElement) {
          const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
          const heightDiff = newHeight - oldHeight;
          window.scrollTo({
            top: scrollTop + heightDiff,
            behavior: 'instant',
          });
        }

        // Drop the newest page off the bottom now that an older one is on the top. Trimming below
        // the viewport needs no scroll compensation.
        this.#trimPages('end');

        requestIdleCallback(() => {
          this.#fetchPage('previous');
        });

        return;
      }
    } finally {
      // Always release the lock, even if an error occurs mid-render
      this.#isRendering = false;
    }
  }

  /**
   * Recycles pages out of the DOM once more than {@link MAX_PAGES_IN_DOM} are rendered, so memory
   * stays flat however far the user scrolls. Trimmed pages are re-inserted by
   * `#renderPreviousPage` / `#renderNextPage` when the user scrolls back towards them.
   *
   * @param {'start' | 'end'} from - Which end of the rendered range to drop.
   */
  #trimPages(from) {
    const { grid } = this.refs;
    if (!grid) return;

    while (this.#pagesInDom.length > MAX_PAGES_IN_DOM) {
      const page = from === 'start' ? this.#pagesInDom[0] : this.#pagesInDom[this.#pagesInDom.length - 1];
      const items = grid.querySelectorAll(`:scope > [data-page="${page}"]`);

      if (!items.length) {
        // Bookkeeping drifted from the DOM. Drop the entry rather than spinning on it.
        if (from === 'start') this.#pagesInDom.shift();
        else this.#pagesInDom.pop();
        continue;
      }

      if (from === 'start') {
        // Removing content above the viewport pulls everything up. Measure a stable anchor either
        // side of the removal and put the scroll position back. Reading the rect forces layout, so
        // if the browser's own scroll anchoring already compensated, the delta is 0 and we no-op.
        const anchor = grid.lastElementChild;
        const before = anchor?.getBoundingClientRect().top ?? 0;

        for (const item of items) item.remove();
        this.#pagesInDom.shift();

        if (anchor) {
          const delta = anchor.getBoundingClientRect().top - before;
          if (delta) window.scrollBy({ top: delta, behavior: 'instant' });
        }
      } else {
        for (const item of items) item.remove();
        this.#pagesInDom.pop();
      }
    }
  }

  /**
   * Seeds `#pagesInDom` from what is actually rendered. Used on connect and after a filter change.
   */
  #syncPagesInDom() {
    const { grid } = this.refs;
    /** @type {Set<number>} */
    const pages = new Set();

    for (const child of grid?.children ?? []) {
      const page = Number(child.getAttribute('data-page'));
      if (Number.isInteger(page) && page > 0) pages.add(page);
    }

    if (!pages.size) {
      // Nothing to read from (an empty result set, say). Fall back to the URL so a deep link such
      // as ?page=3 still knows where it is.
      const page = Number(new URL(window.location.href).searchParams.get('page'));
      pages.add(Number.isInteger(page) && page > 0 ? page : 1);
    }

    this.#pagesInDom = [...pages].sort((a, b) => a - b);
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    if (!this.#pagesInDom.length) this.#syncPagesInDom();

    const isPrevious = type === 'previous';
    const step = isPrevious ? -1 : 1;
    const boundary = isPrevious ? this.#pagesInDom[0] : this.#pagesInDom[this.#pagesInDom.length - 1];

    if (boundary == null) return;

    const lastPage = Number(this.refs.grid?.dataset.lastPage) || Infinity;

    // Step past any pages already known to render nothing, so a fully filtered-out page cannot
    // stall pagination. `#exhaustedPages` is finite, so this terminates.
    let page = boundary + step;
    while (this.#exhaustedPages.has(page) && page >= 1 && page <= lastPage) {
      page += step;
    }

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.pages.clear();
    this.#exhaustedPages.clear();
    this.#pagesInDom = [];

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Reset the rendering lock in case a filter fires mid-render
    this.#isRendering = false;

    // Clear any pending debounce timer
    if (this.#scrollDebounceTimer) {
      clearTimeout(this.#scrollDebounceTimer);
      this.#scrollDebounceTimer = null;
    }

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      // Check if data-last-page changed
      const newLastPage = this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        this.#syncPagesInDom();
        this.#observeViewMore();

        // Fetch the next page
        this.#fetchPage('next');
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };
}
